// © Microsoft Corporation. All rights reserved.
import { __awaiter } from "tslib";
import { IdbTransactionError } from "../AppErrors";
import { environment } from "./Environment";
import { logger } from "./Logger";
/**
 * Provides utility functions to get and set data from IndexedDB.
 * If dbName is not provied, this class creates a db with name MicrosoftNews.
 * If storeName is not provied, this class creates a table/store with name Default.
 * Recommended to create a new table for your feature to separate and iterate through entries quickly.
 * IndexedDB utility does not require stringify and json parsing so more efficient! Just store your object as value.
 */
export class IndexedDb {
    constructor() {
        /** IndexedDb object */
        this.idb = indexedDB;
    }
    /**
     * Returns if IndexedDb is supported.
     * @returns true if supported else false.
     */
    get supported() {
        if (!this.idb) {
            logger.info("indexedDB is not supported.");
            return false;
        }
        return true;
    }
    /**
     * Function looks at query string param and check if IndexedDb operation is allowed or not. By default it is allowed.
     * @returns true if supported else false.
     */
    get isCachingAllowed() {
        const param = environment.getQueryParameter("enableIndexedDbOperation");
        if (param) {
            return param.toLocaleLowerCase() === "true";
        }
        return true;
    }
    /**
     * Function looks at query string param and check if IndexedDb operation is allowed or not. By default it is allowed.
     * @returns true if supported else false.
     */
    get isGetAllAllowed() {
        const param = environment.getQueryParameter("allowGetAllOverCursor");
        if (param) {
            return param.toLocaleLowerCase() === "true";
        }
        if (IDBObjectStore.prototype && IDBObjectStore.prototype.getAll) {
            return true;
        }
        return false;
    }
    /**
     * This should be used for unit testing only.
     * @public
     * @param {IDBFactory} idb - IndexedDB object to override.
     */
    setIndexedDb(idb) {
        this.idb = idb;
    }
    /**
     * This should be used for unit testing only.
     * @public
     * @return {Promise<IDBDatabase>} existing promise.
     */
    getIdbPromise() {
        return this.idbPromise;
    }
    /**
     * Returns object from IndexedDB
     * @public
     * @param {IDBValidKey} key - IndexedDB key. Typically it can be string or any supported type.
     * @returns Promise<Type>
     */
    getObject(key) {
        return __awaiter(this, void 0, void 0, function* () {
            let req;
            yield this.withIdbStore("readonly", store => {
                req = store.get(key);
            });
            return req.result;
        });
    }
    /**
     * Return all objects from IndexedDB using getAll implementation.
     * @public
     * @returns Promise<Type>
     */
    getAll() {
        return __awaiter(this, void 0, void 0, function* () {
            let req;
            yield this.withIdbStore("readonly", store => {
                req = store.getAll();
            });
            return req.result;
        });
    }
    /**
     * Returns all objects from IndexedDB.
     * if getAll is supported, call it else fallback to Cursor implementation that exists on wide range of devices.
     * @public
     * @returns Promise<any>
     */
    getAllObjects() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isGetAllAllowed) {
                return this.getAll();
            }
            const data = [];
            yield this.withIdbStore("readonly", store => {
                store.openCursor().onsuccess = function (event) {
                    const cursor = event.target && event.target.result;
                    if (cursor && cursor.value) {
                        data.push(cursor.value);
                        cursor.continue();
                    }
                };
            });
            return data;
        });
    }
    /**
     * Sets object into IndexedDB
     * @public
     * @param {IDBValidKey} key - IndexedDB key. Typically it can be string or any supported type.
     * @param {any} value - Any typed value you want to insert into IndexedDB.
     */
    setObject(key, value) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.withIdbStore("readwrite", storeCallback => {
                storeCallback.put(value, key);
            });
        });
    }
    /**
     * Removes object from IndexedDB
     * @public
     * @param {IDBValidKey} key - IndexedDB key. Typically it can be string or any supported type.
     */
    removeObject(key) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.withIdbStore("readwrite", storeCallback => {
                storeCallback.delete(key);
            });
        });
    }
    /**
     * Removes all objects from IndexedDB
     * @public
     */
    clearObjects() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.withIdbStore("readwrite", storeCallback => {
                storeCallback.clear();
            });
        });
    }
    /**
     * Initialize IndexedDb.
     * @param idbStoreName {string} - IndexedDB store name
     * @param dbName {string} - Database name
     */
    initializeIndexedDb(idbVersion = 1, idbStoreName = "Default", dbName = "MicrosoftNews") {
        return __awaiter(this, void 0, void 0, function* () {
            this.storeName = idbStoreName;
            this.idbPromise = new Promise((resolve, reject) => {
                const openreq = this.idb.open(dbName, idbVersion);
                openreq.onerror = () => {
                    reject(openreq.error);
                };
                openreq.onsuccess = () => {
                    resolve(openreq.result);
                };
                openreq.onupgradeneeded = () => {
                    openreq.result.createObjectStore(idbStoreName);
                };
            });
        });
    }
    /** Private function to set transaction. */
    withIdbStore(type, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.idbPromise.then(db => new Promise((resolve, reject) => {
                try {
                    const transaction = db.transaction(this.storeName, type);
                    transaction.oncomplete = () => resolve();
                    transaction.onabort = transaction.onerror = () => reject(transaction.error);
                    callback(transaction.objectStore(this.storeName));
                }
                catch (ex) {
                    logger.error(`Error executing transaction: ${ex}`);
                }
            }));
        });
    }
}
/**
 * Deletes Indexed DB
 * @param dbName - data base name to delete
 */
export function deleteIndexedDB(dbName) {
    try {
        const deleteRequest = indexedDB.deleteDatabase(dbName);
        deleteRequest.onerror = function (event) {
            logger.error(`WebWorker.deleteIndexedDB: onerror invoked for [${dbName}]. Error event - ${JSON.stringify(event)}`);
        };
        deleteRequest.onsuccess = function (event) {
            // event.result is expected to be null/undefined, log error if it's not
            if (event.result) {
                logger.error(`WebWorker.deleteIndexedDB: onsuccess returned [${event.result}] for DB [${dbName}]`);
            }
        };
    }
    catch (ex) {
        logger.error(Object.assign(Object.assign({}, IdbTransactionError), { message: "Error executing Idb transaction", pb: Object.assign(Object.assign({}, IdbTransactionError.pb), { customMessage: `Error: ${ex}` }) }));
    }
}
//# sourceMappingURL=IndexedDb.js.map